import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, CheckCircle2, AlertTriangle, Smartphone, MapPin, Clock, Filter } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  success: { label: 'Success', color: '#16A34A', bg: '#16A34A15' },
  password_failed: { label: 'Password failed', color: '#DC2626', bg: '#DC262615' },
  totp_failed: { label: '2FA failed', color: '#DC2626', bg: '#DC262615' },
  locked_out: { label: 'Locked out', color: '#B91C1C', bg: '#B91C1C15' },
};

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function LoginActivityPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ events: [], summary_24h: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', email: '', new_device_only: false });

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.status) p.set('status', filters.status);
    if (filters.email) p.set('email', filters.email);
    if (filters.new_device_only) p.set('new_device_only', 'true');
    const r = await fetch(`${API}/login-activity?${p.toString()}`, { credentials: 'include' });
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (!user) return null;
  const s = data.summary_24h || {};
  const failCount = (s.password_failed || 0) + (s.totp_failed || 0) + (s.locked_out || 0);

  return (
    <div className="min-h-screen bg-background" data-testid="login-activity-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/dashboard" data-testid="la-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: '#DC262633', background: 'linear-gradient(135deg, #DC26260A, transparent 60%)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>
              <ShieldAlert className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#DC26261A', color: '#DC2626' }}>Security · Login Activity</span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Who's logging in</h1>
          <p className="text-sm text-muted-foreground mt-1">Every login attempt — success, failure, or lockout — is logged here. You're emailed on every failure and on new-device logins.</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Kpi label="Successes (24h)" value={s.success || 0} color="#16A34A" icon={CheckCircle2} />
          <Kpi label="Failures (24h)" value={failCount} color={failCount ? '#DC2626' : '#94A3B8'} icon={AlertTriangle} />
          <Kpi label="Lockouts (24h)" value={s.locked_out || 0} color={(s.locked_out || 0) ? '#B91C1C' : '#94A3B8'} icon={ShieldAlert} />
          <Kpi label="New-device (total)" value={data.events.filter(e => e.new_device).length} color="#D97706" icon={Smartphone} />
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border bg-card p-4 mb-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground"><Filter className="w-3.5 h-3.5" />Filters</div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Status</label>
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="la-filter-status" className="text-sm bg-background border border-border rounded-lg px-2 py-1.5">
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="password_failed">Password failed</option>
              <option value="totp_failed">2FA failed</option>
              <option value="locked_out">Locked out</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Email contains</label>
            <input value={filters.email} onChange={e => setFilters({ ...filters, email: e.target.value })} placeholder="e.g. staff1" data-testid="la-filter-email" className="text-sm bg-background border border-border rounded-lg px-2 py-1.5" />
          </div>
          <label className="flex items-center gap-1.5 text-sm text-foreground pb-1">
            <input type="checkbox" checked={filters.new_device_only} onChange={e => setFilters({ ...filters, new_device_only: e.target.checked })} data-testid="la-filter-newdevice" />
            New device only
          </label>
          <button onClick={() => setFilters({ status: '', email: '', new_device_only: false })} data-testid="la-filter-clear" className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border text-foreground">
            Clear
          </button>
        </div>

        {/* Events */}
        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="la-events-table">
          {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && data.events.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">No login events match your filters.</div>
          )}
          {!loading && data.events.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">Device</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e, i) => {
                  const m = STATUS_META[e.status] || { label: e.status, color: '#64748B', bg: '#64748B15' };
                  return (
                    <tr key={i} className="border-b border-border/60 last:border-0" data-testid={`la-row-${i}`}>
                      <td className="px-3 py-2 text-foreground whitespace-nowrap"><Clock className="inline w-3 h-3 mr-1 text-muted-foreground" />{fmt(e.created_at)}</td>
                      <td className="px-3 py-2">
                        <p className="font-medium text-foreground">{e.user_email}</p>
                        <p className="text-[11px] text-muted-foreground">{e.name || '—'} · {e.role || 'unknown'}</p>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: m.bg, color: m.color }}>
                          {m.label}
                        </span>
                        {e.new_device && (
                          <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#D9770615', color: '#D97706' }}>
                            <Smartphone className="w-2.5 h-2.5" /> NEW DEVICE
                          </span>
                        )}
                        {e.reason && <p className="text-[11px] text-muted-foreground mt-0.5">{e.reason}</p>}
                      </td>
                      <td className="px-3 py-2 text-foreground font-mono text-xs"><MapPin className="inline w-3 h-3 mr-0.5 text-muted-foreground" />{e.ip || '—'}</td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground max-w-[260px] truncate" title={e.user_agent}>{e.user_agent || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className="font-['Outfit'] text-2xl font-black" style={{ color }}>{value}</p>
    </div>
  );
}
